import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, map, Observable, Subscription } from 'rxjs';

import { ProjectGroupInfo, ProjectInfo, UserInfo } from '@blaze-shared/api-contracts-domain';
import { ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import { LinkedBlazeTableConnector } from '@blaze-shared/datasets-domain';
import { LocalStorageService, StorageKey } from '@blaze-shared/platform-data-access';
import { compareAscBy } from '@blaze-shared/platform-util';
import {
  ProjectGroupsCollectionService,
  ProjectsCollectionService,
} from '@blaze-shared/projects-data-access';
import {
  EnvironmentIndicatorComponent,
  GroupedProjectsListComponent,
  SharedModule,
} from '@blaze-shared/shared-feature';
import { ButtonBarComponent, FilterInputComponent, PanelComponent } from '@blaze-shared/shared-ui';

import { DataConnectorsStore } from '../../+state/data-connectors.store';

@UntilDestroy()
@Component({
  selector: 'blaze-linked-blaze-table-dialog',
  imports: [
    AsyncPipe,
    ButtonBarComponent,
    EnvironmentIndicatorComponent,
    FilterInputComponent,
    SharedModule,
    PanelComponent,
    GroupedProjectsListComponent,
  ],
  templateUrl: './linked-blaze-table-dialog.component.html',
  styleUrls: ['./linked-blaze-table-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'data-connector-dialog' },
})
export class LinkedBlazeTableDialogComponent implements OnInit {
  private localStorageService = inject(LocalStorageService);
  private projectContextFacade = inject(ProjectContextFacade);
  private projectsCollService = inject(ProjectsCollectionService);
  private readonly dataConnectorsStore = inject(DataConnectorsStore);
  private projectGroupsCollectionService = inject(ProjectGroupsCollectionService);

  hideGroups = input<string[]>();
  showNextPage = output<LinkedBlazeTableConnector>();

  accountId = '';
  user: UserInfo | null = null;
  projects$: Observable<ProjectInfo[]>;
  filteredProjects$: Observable<ProjectInfo[]>;
  getTablesSubscription: Subscription | undefined;
  projectGroups$: Observable<ProjectGroupInfo[]>;

  constructor() {
    this.accountId = this.localStorageService.get(StorageKey.AccountId) || '';
    this.user = this.projectContextFacade.userInfo || null;

    this.projects$ = combineLatest([
      this.projectsCollService.entities$,
      this.dataConnectorsStore.projectId$,
    ]).pipe(
      untilDestroyed(this),
      map(([projects, loadedProjectId]) =>
        projects.filter((p) => p.id !== loadedProjectId).toSorted(compareAscBy('name')),
      ),
    );
    this.filteredProjects$ = this.projects$;
  }

  ngOnInit(): void {
    this.projectGroups$ = this.projectGroupsCollectionService.entities$.pipe(
      map((groups) => {
        // BL-9742: do not allow linking of datasets within the same project group.
        return groups.filter((g) => !(this.hideGroups() || []).includes(g.id));
      }),
    );
  }

  setFilteredProjects(projects$: Observable<ProjectInfo[]>): void {
    this.filteredProjects$ = projects$;
  }

  onProjectClick(project: ProjectInfo): void {
    if (this.getTablesSubscription) {
      this.getTablesSubscription.unsubscribe();
    }
    this.showNextPage.emit({
      name: '',
      projectId: project.id || '',
      projectName: project.name,
      tables: [],
    });
  }
}
